import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Check, Crown, ArrowLeft, Loader2, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PricingPage() {
  const { t, language } = useI18n();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loadingCheckout, setLoadingCheckout] = useState(null); // 'pro' or 'premium'
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [config, setConfig] = useState({
    free_max_recipes: 20,
    free_max_menus: 5,
    pro_monthly_price: 5.99,
    premium_monthly_price: 9.99,
    premium_yearly_price: 39.99,
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API}/config/public`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      // Use defaults
    }
  };

  const handleSubscribe = async (tier) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoadingCheckout(tier);

    try {
      const response = await fetch(`${API}/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tier: tier,
          plan_type: billingPeriod,
          origin_url: window.location.origin,
        }),
      });

      let data;
      try {
        const responseClone = response.clone();
        data = await responseClone.json();
      } catch (e) {
        console.error('Error parsing response JSON:', e);
        data = { detail: t('errors.generic') };
      }

      if (response.ok) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail === 'payment_failed' ? t('errors.paymentFailed') : t('errors.generic'));
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoadingCheckout(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setManagingSubscription(true);
    try {
      const response = await fetch(`${API}/payments/create-portal-session`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        toast.error(t('errors.generic'));
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(t('errors.generic'));
    } finally {
      setManagingSubscription(false);
    }
  };

  const handleFreePlan = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  // Calculate yearly prices
  const proYearlyPrice = (config.pro_monthly_price * 12) * (1 - (config.pro_yearly_discount || 0) / 100);
  const premiumYearlyPrice = (config.premium_monthly_price * 12) * (1 - (config.premium_yearly_discount || 0) / 100);

  const isSubscriber = user?.plan === 'pro' || user?.plan === 'premium';

  return (
    <div className="min-h-screen bg-cream texture-noise">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            className="text-stone hover:text-charcoal"
            data-testid="back-from-pricing-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {t('back')}
          </Button>
          <h1 className="font-manrope font-bold text-xl text-charcoal">MealPlanner</h1>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="px-6 py-12 md:py-20 max-w-[1200px] mx-auto">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="font-manrope font-bold text-3xl md:text-4xl text-charcoal mb-4">
            {t('pricing.title')}
          </h1>
          <p className="font-inter text-lg text-stone max-w-xl mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-full p-1 shadow-soft inline-flex">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-3 rounded-full font-manrope font-semibold transition-all ${billingPeriod === 'monthly'
                ? 'bg-primary text-white'
                : 'text-stone hover:text-charcoal'
                }`}
              data-testid="monthly-toggle"
            >
              {language === 'es' ? 'Mensual' : 'Monthly'}
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-3 rounded-full font-manrope font-semibold transition-all relative ${billingPeriod === 'yearly'
                ? 'bg-primary text-white'
                : 'text-stone hover:text-charcoal'
                }`}
              data-testid="yearly-toggle"
            >
              {language === 'es' ? 'Anual' : 'Yearly'}
              {config.premium_yearly_discount > 0 && (
                <span className="absolute -top-2 -right-2 bg-sage text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                  {language === 'es' ? `AHORRA ${config.premium_yearly_discount}%` : `SAVE ${config.premium_yearly_discount}%`}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Free Plan */}
          <Card className="bg-white rounded-3xl shadow-soft p-8 border-2 border-border flex flex-col" data-testid="free-plan-card">
            <div className="flex-1">
              <h2 className="font-manrope font-bold text-2xl text-charcoal mb-0.5">
                {t('pricing.free.title')}
              </h2>
              <div className="mb-6">
                <span className="font-manrope font-bold text-4xl text-charcoal">
                  {t('pricing.free.price')}
                </span>
                <span className="font-inter text-stone ml-2">
                  {t('pricing.free.period')}
                </span>
              </div>

              <ul className="space-y-4 mb-8">
                {t('pricing.free.features', { recipes: config.free_max_recipes, menus: config.free_max_menus }).map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="font-inter text-charcoal">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={handleFreePlan}
              variant="outline"
              className="w-full rounded-full py-6 font-manrope font-bold border-2"
              data-testid="free-plan-button"
            >
              {user?.plan === 'free' ? (language === 'es' ? 'Plan actual' : 'Current plan') : t('pricing.free.cta')}
            </Button>
          </Card>

          {/* Pro Plan */}
          <Card className="bg-white rounded-3xl shadow-soft p-8 border-2 border-sage flex flex-col relative overflow-hidden" data-testid="pro-plan-card">
            {/* Top badges */}
            <div className="absolute top-4 right-4 flex items-center gap-2">
              {billingPeriod === 'yearly' && config.pro_yearly_discount > 0 && (
                <span className="bg-sage/10 text-sage text-[10px] font-manrope font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                  -{config.pro_yearly_discount}%
                </span>
              )}
            </div>

            <div className="flex-1">
              <h2 className="font-manrope font-bold text-2xl text-charcoal mb-0.5">
                {t('pricing.pro.title')}
              </h2>
              <div className="mb-6">
                <span className="font-manrope font-bold text-4xl text-charcoal">
                  {billingPeriod === 'monthly'
                    ? `£${config.pro_monthly_price.toFixed(2)}`
                    : `£${proYearlyPrice.toFixed(2)}`}
                </span>
                <span className="font-inter text-stone ml-2 font-medium">
                  {billingPeriod === 'monthly' ? t('pricing.pro.monthlyPeriod') : t('pricing.pro.yearlyPeriod')}
                </span>
              </div>

              <ul className="space-y-4 mb-8">
                {t('pricing.pro.features').map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="font-inter text-charcoal">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={() => isSubscriber && user?.plan !== 'pro' ? handleManageSubscription() : handleSubscribe('pro')}
              disabled={loadingCheckout === 'pro' || user?.plan === 'pro' || managingSubscription}
              className="w-full bg-sage text-white hover:bg-sage/90 rounded-full py-6 font-manrope font-bold shadow-md hover:shadow-lg transition-all"
              data-testid="pro-plan-button"
            >
              {loadingCheckout === 'pro' || managingSubscription ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : user?.plan === 'pro' ? (
                language === 'es' ? 'Plan actual' : 'Current plan'
              ) : isSubscriber ? (
                language === 'es' ? 'Cambiar Plan' : 'Switch Plan'
              ) : (
                t('pricing.pro.cta')
              )}
            </Button>
          </Card>

          {/* Premium Plan */}
          <Card className="bg-charcoal text-white rounded-3xl shadow-lift p-8 border-2 border-primary flex flex-col relative overflow-hidden" data-testid="premium-plan-card">
            {/* Popular/Discount badge */}
            <div className="absolute top-4 right-4 flex items-center gap-2">
              {billingPeriod === 'yearly' && config.premium_yearly_discount > 0 && (
                <span className="bg-white/10 text-white text-[10px] font-manrope font-bold px-2 py-1 rounded-full uppercase tracking-wider order-2">
                  -{config.premium_yearly_discount}%
                </span>
              )}
              <span className="bg-primary text-white text-[10px] font-manrope font-bold px-2 py-1 rounded-full flex items-center gap-1 uppercase tracking-wider order-1">
                <Sparkles className="w-3 h-3" />
                Premium
              </span>
            </div>

            <div className="flex-1">
              <h2 className="font-manrope font-bold text-2xl mb-0.5 text-white">
                {t('pricing.premium.title')}
              </h2>
              <div className="mb-6">
                <span className="font-manrope font-bold text-4xl text-white">
                  {billingPeriod === 'monthly'
                    ? `£${config.premium_monthly_price.toFixed(2)}`
                    : `£${premiumYearlyPrice.toFixed(2)}`}
                </span>
                <span className="font-inter text-primary ml-2 font-medium">
                  {billingPeriod === 'monthly' ? t('pricing.premium.monthlyPeriod') : t('pricing.premium.yearlyPeriod')}
                </span>
              </div>

              <ul className="space-y-4 mb-8">
                {[
                  ...t('pricing.premium.features'),
                  ...(config?.recipe_video_import_enabled ? [t('pricing.premium.videoImportFeature')] : []),
                  ...(config?.pexels_enabled ? [t('pricing.premium.aiImagesFeature')] : []),
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="font-inter text-white/90">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={() => isSubscriber && user?.plan !== 'premium' ? handleManageSubscription() : handleSubscribe('premium')}
              disabled={loadingCheckout === 'premium' || user?.plan === 'premium' || managingSubscription}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full py-6 font-manrope font-bold shadow-lg hover:shadow-xl transition-all active:scale-95"
              data-testid="premium-plan-button"
            >
              {loadingCheckout === 'premium' || managingSubscription ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : user?.plan === 'premium' ? (
                language === 'es' ? 'Plan actual' : 'Current plan'
              ) : isSubscriber ? (
                language === 'es' ? 'Cambiar Plan' : 'Switch Plan'
              ) : (
                t('pricing.premium.cta')
              )}
            </Button>
          </Card>
        </div>

        {/* Support info */}
        <div className="text-center mt-12 bg-white/50 backdrop-blur-sm rounded-2xl p-6 border border-border/50">
          <p className="font-inter text-stone">
            {language === 'es'
              ? '¿Necesitas ayuda para elegir o tienes una consulta personalizada?'
              : 'Need help choosing or have a personalized inquiry?'}
            <br />
            <a href="mailto:info@mealplanner.es" className="text-primary font-semibold hover:underline mt-1 inline-block">
              info@mealplanner.es
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
